import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { usePatientConsentsKey } from "@/api2/Consent/keyFactory";
import { useAppendConsent } from "@/api2/Consent/mutations/useAppendConsent";
import { usePolicyMetadataQuery } from "@/api2/Consent/queries/usePolicyMetadata";
import { ADVANCED_CARE_PROGRAM, ConsentedPolicyEnum } from "@/core/constants";
import type { PatientConsent, UUID } from "@/core/types";
import { captureErrorMessage, parseError } from "@/lib/helpers/captureErrorMessage";
import { cn } from "@/lib/helpers/cn";
import { getConsentPayload } from "@/lib/helpers/getConsentPayload";
import { showToast } from "@/providers/ToastProvider";
import { Button2 } from "@/ui2/atoms/Button";

import { OptOutConfirmationModal } from "./OptOutConfirmationModal";

type OptOutButtonProps = {
  patientId: UUID;
  patientName: string;
  sortKey: string;
};

const policyType = ADVANCED_CARE_PROGRAM;

export function OptOutButton({ patientId, patientName, sortKey }: OptOutButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { mutate: appendConsent } = useOptimisticOptOut({
    patientId,
    patientName,
    sortKey,
  });

  const { data: policyMetadata, isLoading: isLoadingPolicyMetadata } = usePolicyMetadataQuery();

  const handleOptOut = async () => {
    if (!policyMetadata) return;

    const consentPayload = getConsentPayload({
      consentGivenBy: patientId,
      consentResponse: false,
      consentType: ConsentedPolicyEnum[policyType],
      patientId,
      policyMetadata,
      policyVersion: policyMetadata[policyType].current_version,
    });

    appendConsent(consentPayload);
    setIsModalOpen(false);
  };

  return (
    <>
      <Button2
        className={cn(
          "tw:border-transparent",
          "tw:text-text-error-primary",
          "tw:hover:text-text-error-secondary",
          "tw:active:text-text-error-primary",
        )}
        disabled={isLoadingPolicyMetadata}
        onPress={() => setIsModalOpen(true)}
        size="sm"
        variant="text"
      >
        Opt Out
      </Button2>

      <OptOutConfirmationModal
        isModalOpen={isModalOpen}
        onCancelPress={() => setIsModalOpen(false)}
        onConfirmPress={handleOptOut}
        onOpenChange={setIsModalOpen}
        patientName={patientName}
      />
    </>
  );
}

function useOptimisticOptOut({ patientId, patientName, sortKey }: OptOutButtonProps) {
  const queryClient = useQueryClient();

  return useAppendConsent({
    onError: (error, _variables, context) => {
      // Guard: Only rollback if we have previous data
      if (context?.previousConsents) {
        queryClient.setQueryData(usePatientConsentsKey.list(patientId), context.previousConsents);
      }

      showToast([
        {
          heading: "Something went wrong. Please reload the page and try again.",
          variant: "error",
        },
      ]);

      captureErrorMessage(parseError({ error, fallback: "ACP opt-out error" }), {
        tags: {
          component: "OptOutButton",
          consentType: ADVANCED_CARE_PROGRAM,
          errorType: "mutation",
          patientId,
          patientName,
        },
      });
    },

    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: usePatientConsentsKey.list(patientId) });

      const previousConsents = queryClient.getQueryData<PatientConsent[]>(
        usePatientConsentsKey.list(patientId),
      );

      // Guard: Return early if no data exists
      if (!previousConsents) {
        return { previousConsents: undefined, updatedConsent: undefined };
      }

      // Find the consent to update
      const consentToUpdate = previousConsents.find((c) => c.sort_key === sortKey);

      // Guard: Return early if consent not found
      if (!consentToUpdate) {
        return { previousConsents, updatedConsent: undefined };
      }

      const updatedConsent = {
        ...consentToUpdate,
        consent_response: false,
      };

      // Replace the consent in the array, don't append
      const updatedConsents = previousConsents.map((consent) =>
        consent.sort_key === sortKey ? updatedConsent : consent,
      );

      queryClient.setQueryData(usePatientConsentsKey.list(patientId), updatedConsents);

      return { previousConsents, updatedConsent };
    },

    onSuccess: () => {
      showToast([
        {
          heading: `${patientName} successfully opted out of the Advanced Care Program.`,
          variant: "success",
        },
      ]);
    },
    /** NOTE: We don't want to invalidate cache onSettled function - by design BE needs a few seconds to update the consents table.
     * If we do invalidate the cache immediately, the user will see the old consent state.
     */
  });
}
